/* eslint-disable max-lines-per-function */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { RichTextReadOnly } from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import { EditableTable } from "components/haven-design/editable-table";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import logger from "services/logger";
import { deletePlan } from "types/gql-types";
import { GET_PLANS_ALL, DELETE_PLAN } from "./queries";

const PlansIndex: React.FC = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const { loading, error, data } = useQuery(GET_PLANS_ALL, {
    fetchPolicy: "no-cache",
  });
  const [deletePlanFunc] = useMutation<deletePlan>(DELETE_PLAN);

  const handleDelete = async (planId: string, planName: string) => {
    const confirmMessage =
      `Are you sure you want to delete the plan "${planName}"? ` +
      "This action cannot be undone.";
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deletePlanFunc({
        variables: { id: planId },
        refetchQueries: ["plansAll"],
      });
      openSnackbar({ message: "Plan deleted successfully", status: "success" });
    } catch (err: any) {
      logger.sendError(err);
      openSnackbar({ message: "Failed to delete plan", status: "error" });
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!data) return <div>No data available</div>;

  const plans = data.plansAll || [];

  return (
    <EditableTable
      title="Plans"
      isLoading={loading}
      data={plans}
      options={{
        pageSize: 20,
        pageSizeOptions: [10, 20, 50],
        search: true,
        sorting: true,
      }}
      columns={[
        {
          field: "name",
          title: "Name",
          defaultSort: "asc",
          render: (rowData: any) => <strong>{rowData.name}</strong>,
        },
        {
          field: "description",
          title: "Description",
          render: (rowData: any) => (
            <RichTextReadOnly
              content={rowData.description}
              extensions={[StarterKit]}
            />
          ),
        },
        {
          field: "isPublic",
          title: "Status",
          render: (rowData: any) => (
            <span
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
                backgroundColor: rowData.isPublic ? "#e3f2fd" : "#f5f5f5",
                color: rowData.isPublic ? "#1976d2" : "#666",
              }}
            >
              {rowData.isPublic ? "Public" : "Private"}
            </span>
          ),
        },
        {
          field: "defaultPricing",
          title: "Default Pricing",
          render: (rowData: any) => {
            if (rowData.defaultPricing) {
              return (
                <div>

                  <div>
                    {rowData.defaultPricing.pricingType?.name || "Unknown"}
                  </div>
                  <div style={{ fontSize: "0.8em", color: "#666" }}>
                    ${rowData.defaultPricing.minimumPrice || "0"}
                    {rowData.defaultPricing.unitThreshold &&
                      ` (${rowData.defaultPricing.unitThreshold} units)`}
                    {rowData.defaultPricing.monthlyPricePerUnit != null &&
                      ` · $${rowData.defaultPricing.monthlyPricePerUnit}/unit`}
                  </div>
                </div>
              );
            }
            return <span style={{ color: "#999" }}>No pricing set</span>;
          },
        },
        {
          field: "insertedAt",
          title: "Created",
          type: "datetime",
          render: (rowData: any) =>
            (rowData.insertedAt ? new Date(rowData.insertedAt).toLocaleDateString() : "Unknown"),
        },
      ]}
      actions={[
        {
          icon: "add",
          tooltip: "Create Plan",
          isFreeAction: true,
          onClick: () => navigate("/dash/admin/plans/new"),
        },
      ]}
      editable={{
        onRowDelete: (oldData: any) =>
          new Promise<void>((resolve, reject) => {
            const confirmMessage =
              `Are you sure you want to delete the plan "${oldData.name}"? ` +
              "This action cannot be undone.";
            if (!window.confirm(confirmMessage)) {
              reject(new Error("User cancelled deletion"));
              return;
            }
            handleDelete(oldData.id, oldData.name)
              .then(() => resolve())
              .catch((err) => reject(err));
          }),
      }}
      onRowClick={(_, rowData) => {
        if (rowData) {
          navigate(`/dash/admin/plans/${rowData.id}/edit`);
        }
      }}
    />
  );
};

export default PlansIndex;
